import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button, Badge } from '@/components/ui';
import {
  Copy,
  Download,
  Share2,
  CheckCircle,
  ExternalLink,
  Info,
} from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { useToast } from '@/hooks/use-toast';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface StaticAddress {
  id: string;
  depositAddress: string;
  originNetwork: string;
  originAsset?: string;
  bankAccount: {
    accountNumber: string;
    accountName?: string;
    bankName?: string;
  };
  currency: string;
}

interface AddressQRModalProps {
  open: boolean;
  onClose: () => void;
  address: StaticAddress;
}

const networkInfo: Record<string, { name: string; explorer: string; color: string }> = {
  BASE: {
    name: 'Base',
    explorer: 'https://basescan.org/address/',
    color: 'bg-blue-500'
  },
  POLYGON: {
    name: 'Polygon',
    explorer: 'https://polygonscan.com/address/',
    color: 'bg-purple-500'
  },
  ARB: {
    name: 'Arbitrum',
    explorer: 'https://arbiscan.io/address/',
    color: 'bg-blue-600'
  },
  OP: {
    name: 'Optimism',
    explorer: 'https://optimistic.etherscan.io/address/',
    color: 'bg-red-500'
  },
  SOL: {
    name: 'Solana',
    explorer: 'https://solscan.io/address/',
    color: 'bg-gradient-to-r from-purple-500 to-blue-500'
  },
  AVAX: {
    name: 'Avalanche',
    explorer: 'https://snowtrace.io/address/',
    color: 'bg-red-600'
  },
  BSC: {
    name: 'BNB Chain',
    explorer: 'https://bscscan.com/address/',
    color: 'bg-yellow-500'
  },
};

export function AddressQRModal({ open, onClose, address }: AddressQRModalProps) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast({
      title: 'Copied!',
      description: 'Address copied to clipboard',
    });
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadQR = () => {
    const canvas = document.getElementById('qr-code') as HTMLCanvasElement;
    if (!canvas) return;

    const link = document.createElement('a');
    link.download = `pouchpay-address-${address.originNetwork}.png`;
    link.href = canvas.toDataURL();
    link.click();

    toast({
      title: 'Downloaded!',
      description: 'QR code saved to your device',
    });
  };

  const shareAddress = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `PouchPay ${networkInfo[address.originNetwork]?.name || address.originNetwork} Deposit Address`,
          text: `Send ${address.originAsset || 'crypto'} to: ${address.depositAddress}`,
          url: window.location.origin,
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      copyToClipboard(address.depositAddress);
    }
  };

  const openExplorer = () => {
    const explorerUrl = networkInfo[address.originNetwork]?.explorer;
    if (explorerUrl) {
      window.open(`${explorerUrl}${address.depositAddress}`, '_blank');
    }
  };

  const networkData = networkInfo[address.originNetwork] || {
    name: address.originNetwork,
    color: 'bg-gray-500'
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Badge variant="neutral" className={`${networkData.color} text-white`}>
              {networkData.name}
            </Badge>
            {address.originAsset && (
              <span className="text-base text-gray-600">
                {address.originAsset} Deposit
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            Send {address.originAsset || 'crypto'} to this address to receive {address.currency} in your bank account
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* QR Code */}
          <div className="flex justify-center p-4 bg-white rounded-lg">
            <QRCodeCanvas
              id="qr-code"
              value={address.depositAddress}
              size={200}
              level="H"
              includeMargin={false}
              imageSettings={{
                src: '/logo.png',
                x: undefined,
                y: undefined,
                height: 24,
                width: 24,
                excavate: true,
              }}
            />
          </div>

          {/* Address Display */}
          <div>
            <label className="text-sm font-medium text-gray-600">Deposit Address</label>
            <div className="mt-1 p-3 bg-gray-50 rounded-lg break-all">
              <code className="text-sm font-mono">{address.depositAddress}</code>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="secondary"
              onClick={() => copyToClipboard(address.depositAddress)}
              className="flex items-center gap-2"
            >
              {copied ? (
                <>
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  Copy
                </>
              )}
            </Button>
            <Button
              variant="secondary"
              onClick={downloadQR}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Download QR
            </Button>
            {typeof navigator.share === 'function' && (
              <Button
                variant="secondary"
                onClick={shareAddress}
                className="flex items-center gap-2"
              >
                <Share2 className="h-4 w-4" />
                Share
              </Button>
            )}
            {networkInfo[address.originNetwork]?.explorer && (
              <Button
                variant="secondary"
                onClick={openExplorer}
                className="flex items-center gap-2"
              >
                <ExternalLink className="h-4 w-4" />
                Explorer
              </Button>
            )}
          </div>

          {/* Bank Account Info */}
          <div className="p-3 bg-blue-50 rounded-lg">
            <div className="flex items-start gap-2">
              <Info className="h-4 w-4 text-blue-600 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-blue-900 mb-1">Automatic Settlement</p>
                <p className="text-blue-800">
                  Funds will be sent to: <span className="font-medium">
                    {address.bankAccount.accountNumber}
                  </span>
                  {address.bankAccount.bankName && (
                    <span> ({address.bankAccount.bankName})</span>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Important Notice */}
          <Alert>
            <AlertDescription>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>This is a permanent address - save it for future use</li>
                <li>Only send {address.originAsset || 'supported tokens'} on {networkData.name}</li>
                <li>Minimum deposit: 10 {address.originAsset || 'USDC'}</li>
                <li>Funds arrive in 1-5 minutes after confirmation</li>
              </ul>
            </AlertDescription>
          </Alert>
        </div>
      </DialogContent>
    </Dialog>
  );
}